/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useEffect, useState } from "react"

const MS_PER_MINUTE = 60_000

/**
 * Re-renders the caller once a minute so a derived countdown actually counts down.
 *
 * The Orders table renders its SLA in whole minutes, so a minute is the finest useful
 * cadence — a per-second timer would re-render sixty times per visible change. The first
 * tick is aligned to the next wall-clock minute boundary rather than a full minute from
 * mount, so a row mounted at :59 updates a second later instead of holding a stale value
 * for another minute.
 *
 * Returns nothing: callers derive their own value from `Date.now()`. Handing back a
 * timestamp would invite treating it as the render clock, and every consumer already has
 * a different deadline to subtract from.
 */
export const useMinuteTick = (enabled: boolean = true): void => {
  const [, setTick] = useState(0)

  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    let intervalID: ReturnType<typeof setInterval> | undefined

    const msUntilNextMinute = MS_PER_MINUTE - (Date.now() % MS_PER_MINUTE)
    const timeoutID = setTimeout(() => {
      setTick(previous => previous + 1)
      intervalID = setInterval(
        () => setTick(previous => previous + 1),
        MS_PER_MINUTE,
      )
    }, msUntilNextMinute)

    return () => {
      clearTimeout(timeoutID)
      if (intervalID !== undefined) {
        clearInterval(intervalID)
      }
    }
  }, [enabled])
}
